<script setup>
import {useRouting} from "@/composables/routing.js";
const {href,url} = useRouting();

import { Head } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3';
</script>

<template>
    <Head>
        <title>Terms and Conditions</title>
        <meta name="description" content="Terms and Conditions of the LAY7 cloud">
        <link rel="canonical" :href="url('/legal/tos', 'en')">
        <link rel="alternate" hreflang="de" :href="url('/legal/tos', 'de')">
        <link rel="alternate" hreflang="en" :href="url('/legal/tos', 'en')">
        <meta name="robots" content="noindex, nofollow">
    </Head>
    <section>
        <div class="container px-4 mx-auto mt-28 lg:mt-36">
            <h1 class="font-semibold text-4xl text-black space-x-3 mb-3">
                Terms and Conditions
            </h1>
            <p>
                (as of: 10.01.2025)
            </p>
        </div>
    </section>

    <section class="mt-8 lg:mt-12">
        <div class="container px-4 mx-auto">
            <p class="mt-4 text-black text-lg font-bold">
                1. Scope of Application, Change of Terms and Conditions
            </p>
            <p>
                (1) These terms and conditions (AGB) are valid for all contracts and services between Tobias Holst e.K. (www.LAY7.cloud, hereinafter referred to as the provider) and its customers.
            </p>
            <p>
                (2) These AGB apply to all contracts (orders) concluded between the parties, even if there is no explicit reference to the AGB in them. Deviating general terms and conditions of the customer do not apply, even if the provider does not explicitly object to them. Individual special agreements take precedence over these AGB; this does not apply to pre-formulated contract terms of the customer. The provider reserves the right to change these AGB at any time. The version of the AGB in effect at the time of contract conclusion is decisive for new contracts. For existing customers, the amended AGB apply under the specific requirements of the following provisions. Changes to the agreed AGB for existing customers are only possible under the following restrictions: circumstances that justify such a change have occurred unexpectedly and are beyond the control of the provider and have an impact on one party unilaterally. The provider will send the customer the amended AGB four weeks before they come into effect and specify the circumstances that prompted the change as well as the extent of the amendments. If the customer does not object in writing or by fax before the changes take effect but continues to use the provider’s services, the change is considered accepted. In the event of a timely, properly executed objection, the previous AGB will continue to apply; in this case, both the customer and the provider are entitled to terminate the contract with notice in accordance with section 6.3.
            </p>
            <p class="mt-4 text-black text-lg font-bold">
                2. Conclusion of Contracts, Communication
            </p>
            <p>
                (1) Offers and telephone agreements are generally non-binding, unless otherwise indicated. The offers and service descriptions published on the provider’s website (www.lay7.cloud) are decisive
            </p>
            <p>
                (2) Customer orders are accepted through the web interface provided by the provider at my.lay7.cloud. Ordering via email or phone is excluded.
            </p>
            <p>
                (3) The customer expressly agrees to receive information regarding ongoing contracts via email, including confidential information such as access credentials for services commissioned by the customer.
            </p>
            <p class="mt-4 text-black text-lg font-bold">
                3. Scope of Services
            </p>
            <p>
                (1) The provider is dependent on infrastructure operated by third parties to provide its services and has no control over it. Therefore, disruptions and temporary performance reductions that are not caused by the provider may occur, particularly internet disruptions or force majeure. Unless otherwise explicitly agreed, the provider guarantees an availability of its services of 99.9% on an annual average. Availability is considered to be ensured if the servers and services are essentially operational.
                <br>
                The following circumstances do not constitute operational disruptions:
            </p>
            <ul class="list-disc pl-4">
                <li>
                    Interruptions in accessibility due to issues with third-party services over which the provider has no control.
                </li>
                <li>
                    Interruptions due to force majeure.
                </li>
                <li>
                    Short-term interruptions necessary to prevent or address security risks (e.g., through updates).
                </li>
            </ul>
            <p>
                (2) To maintain the performance of the technology used, the provider regularly conducts maintenance and service work. If these activities result in service interruptions, the provider will try to schedule them during times of low demand and inform the customer in advance. To maintain a high level of security, software updates are regularly applied. This may lead to changes in the scope and design of services provided. It may also require changes to customer-provided content or installed applications. The provider will inform the customer in advance if possible. If such updates require unreasonably significant changes for the customer, the customer can terminate the contract exceptionally. The provider is entitled to have third parties provide the services, either wholly or partially.
            </p>
            <p>
                (3) During the contract period, the customer may request an upgrade to a higher-tier plan at any time. Any fees for the upgrade should be inquired with the provider. Upon the upgrade, a new contract begins with the applicable service description, terms, and conditions, potentially including a new minimum contract term. Unused payments for the previous plan will be refunded when switching to a higher-tier plan.
            </p>
            <p>
                (4) Unless otherwise agreed, the provider offers support services by answering customer inquiries via email, which can be submitted via the contact form on www.lay7.cloud or within the customer portal.
            </p>
            <p class="mt-4 text-black text-lg font-bold">
                4. Payment Terms
            </p>
            <p>
                (1) Unless a different billing method is agreed upon, usage-independent fees are payable in advance for the billing period specified in the service description. Usage-based fees are billed at the end of the billing period. Invoices are due within 14 days.
            </p>
            <p>
                (2) If the customer is in default of payment, the provider can restrict or suspend its services after prior notification, including domains registered for the customer. If the customer is overdue for two consecutive months with a significant portion of the payment or is overdue for an amount corresponding to a monthly fee for a period exceeding two months, the provider may terminate the contract for cause without notice. The customer may only offset claims against the provider with undisputed or legally established counterclaims.
            </p>
            <p>
                (3) The provider is entitled to adjust the prices for the services agreed upon if and to the extent that the costs for providing the services have changed significantly and can be proven. Such changes may result from:
            </p>
            <ul class="list-disc pl-4">
                <li>
                    Increased energy costs,
                </li>
                <li>
                    Higher telecommunications infrastructure costs,
                </li>
                <li>
                    Changed legal or regulatory requirements,
                </li>
                <li>
                    General economic developments that significantly increase the cost of service provision.
                </li>
            </ul>
            <p>
                Price changes will be communicated to the customer in writing (e.g., via email) at least six weeks before the start of the new billing period. The customer is entitled to terminate the contract before the price change takes effect. If the customer does not exercise this right, the new prices will be deemed accepted. The provider will explicitly inform the customer about this in the change notification.
            </p>
            <p>
                (4) The customer expressly agrees that the provider will only provide invoices in electronic form, particularly as a PDF document via email. The invoice will not be sent in paper form. The customer is required to notify the provider of any changes to their email address without delay.
            </p>
            <p class="mt-4 text-black text-lg font-bold">
                5. Customer Obligations
            </p>
            <p>
                (1) The customer is required to provide their personal data correctly when registering and, in case of direct debit authorization, their bank account information. If domain registration or changes are required, the written consent of the domain owner is required. The customer will promptly provide their consent for any changes related to domain status in the necessary form.
            </p>
            <p>
                (2) The customer must change the provided access data immediately and keep it confidential from third parties. The customer is responsible for any misuse of access data by third parties if caused by their negligence.
            </p>
            <p>
                (3) The customer is responsible for making adequate backup copies of the content stored with the provider. Unless otherwise agreed, the provider does not perform backups of customer data.
            </p>
            <p>
                (4) The customer is obligated to comply with applicable legal regulations when using the provider’s services and refrain from actions that could disrupt the operation of the provider's servers. Specifically prohibited actions include:
            </p>
            <ul class="list-disc pl-4">
                <li>
                    Mass emailing to recipients who have not consented (Double-Opt-In process),
                </li>
                <li>
                    Sending emails with falsified sender data (Spoofing),
                </li>
                <li>
                    Storing and distributing data with pornographic, commercial, or copyrighted content without the right to distribute (e.g., "warez" sites, illegal MP3 downloads),
                </li>
                <li>
                    Operating file-sharing services (e.g., Torrents),
                </li>
                <li>
                    Operating "open proxy services" like TOR,
                </li>
                <li>
                    Storing and distributing data with extremist content, hate speech, or promoting terrorism and violence.
                </li>
            </ul>
            <p>
                (5) The customer is aware that the provider does not verify the legality of the content they store, and the customer is responsible for ensuring the content’s legality. The provider is entitled to temporarily block the services if the customer violates these obligations or if there is a suspicion of such violations.
            </p>
            <p>
                (6) Despite taking appropriate care, technical malfunctions are possible. If the customer detects a malfunction in the provider's technical systems, they must promptly report it to the provider with detailed information.
            </p>
            <p class="mt-4 text-black text-lg font-bold">
                6. Contract Duration, Termination
            </p>
            <p>
                (1) Unless otherwise agreed, contracts are concluded for an indefinite period. Such contracts can be terminated by either party with one month's notice to the end of the month, but no earlier than at the end of the agreed minimum contract term.
            </p>
            <p>
                (2) If either party has an extraordinary right to terminate according to these terms, the contract can be terminated with one month's notice to the end of the month, regardless of any agreed minimum contract term.
            </p>
            <p>
                (3) Termination must be made in writing via letter, fax, or the secure online management area (customer portal) if available. Termination by email is permissible if the email address used by the sender is registered for the customer account and contains a signed, digitized document (PDF) indicating the intent to terminate.
            </p>
            <p>
                (4) When the customer terminates the contract, they must specify what should happen to registered domains. If the customer does not provide timely instructions, the provider is entitled to delete the domains.
            </p>
            <p>
                (5) Both parties retain the right to terminate the contract for cause without notice. A significant reason for termination by the provider exists, for example, if:
            </p>
            <ul class="list-disc pl-4">
                <li>
                    The customer is in default of payment for two consecutive periods or for a period exceeding two months,
                </li>
                <li>
                    The customer is insolvent or an insolvency petition has been filed,
                </li>
                <li>
                    The customer violates essential contractual obligations and does not correct the breach after a warning.
                </li>
            </ul>
            <p class="mt-4 text-black text-lg font-bold">
                7. Warranty and Liability
            </p>
            <p>
                (1) The provider is not liable for damages, consequential damages, or loss of profit resulting from disruptions or technical issues in systems outside the provider’s control.
            </p>
            <p>
                (2) The provider is also not liable for damages caused by the customer’s failure to fulfill their contractual obligations, particularly the obligation to back up their data.
            </p>
            <p>
                (3) Liability for defects in the provided storage space that existed at the time of contract conclusion is excluded.
            </p>
            <p>
                (4) The provider is only liable for gross negligence or intent. This does not apply to the violation of essential contractual obligations, where the provider is liable for typical damages that were foreseeable at the time of contract conclusion.
            </p>
            <p>
                (5) The customer is liable for any direct or indirect damages (including loss of profit) resulting from the violation of the obligations specified in section 5. The customer agrees to indemnify the provider from any third-party claims arising from the illegality of content stored by the customer.
            </p>
            <p class="mt-4 text-black text-lg font-bold">
                8. Final Provisions, Jurisdiction, Applicable Law
            </p>
            <p>
                German law applies. The place of jurisdiction is the court responsible for the provider’s headquarters. If individual provisions of this agreement are invalid or unenforceable, this does not affect the validity of the rest of the agreement.
            </p>
        </div>
    </section>
</template>
